import { FunctionComponent, useState } from "react";

import { useTranslation } from "react-i18next";

import { faBookmark, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components/Modals/Modal";
import { PermissionRoles } from "@/enum";
import { useHasPermissions } from "@/utils/useHasPermissions";

import {
	useGetSavedFiltersQuery,
	useCreateSavedFilterMutation,
	useDeleteSavedFilterMutation,
} from "../../redux/savedFilter/savedFilter.api";
import { IFilterWithValues } from "../../pages/List/components/FilterModal";

interface SavedFilterSelectorProps {
	currentFilter?: IFilterWithValues;
	onLoadFilter: (filterConfig: IFilterWithValues) => void;
}

export const SavedFilterSelector: FunctionComponent<SavedFilterSelectorProps> = ({
	currentFilter,
	onLoadFilter,
}) => {
	const { t } = useTranslation();
	const { hasPermissions } = useHasPermissions();

	const [showSaveInput, setShowSaveInput] = useState(false);
	const [filterName, setFilterName] = useState("");
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [filterToDelete, setFilterToDelete] = useState<number | null>(null);

	const { data: savedFiltersResponse } = useGetSavedFiltersQuery();
	const [createSavedFilter, { isLoading: isCreating }] = useCreateSavedFilterMutation();
	const [deleteSavedFilter] = useDeleteSavedFilterMutation();

	if (!hasPermissions([PermissionRoles.SAVED_FILTER])) {
		return null;
	}

	const savedFilters = savedFiltersResponse?.data ?? [];

	const hasActiveFilters = currentFilter && Object.keys(currentFilter).length > 0;

	const handleSaveFilter = async () => {
		if (!filterName.trim() || !hasActiveFilters) {
			return;
		}

		await createSavedFilter({
			name: filterName.trim(),
			filterConfig: currentFilter as unknown as Record<string, unknown>,
		});

		setFilterName("");
		setShowSaveInput(false);
	};

	const handleDeleteFilter = async (id: number) => {
		await deleteSavedFilter(id);
	};

	const handleLoadFilter = (filterConfig: Record<string, unknown>) => {
		onLoadFilter(filterConfig as unknown as IFilterWithValues);
	};

	return (
		<div className="mb-4 border-b border-border pb-4">
			<div className="flex items-center justify-between mb-2">
				<span className="text-sm font-medium text-gray-700">{t("basics.savedFilter.title")}</span>
				<div className="w-fit">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						size={ButtonSize.XS}
						title={t("basics.savedFilter.save")}
						onClick={() => setShowSaveInput(!showSaveInput)}
					/>
				</div>
			</div>

			{showSaveInput && (
				<div className="flex items-center gap-2 mb-3 max-w-[50%]">
					<div className="w-[80%]">
						<InputField
							className="-mb-3 rounded-[10px]"
							handleChange={(e) => setFilterName(e.target.value)}
							name="savedFilterName"
							placeholder={t("basics.savedFilter.namePlaceholder")}
							size={ButtonSize.XS}
							value={filterName}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.BRAND}
							disabled={!filterName.trim() || !hasActiveFilters}
							isLoading={isCreating}
							size={ButtonSize.XS}
							title={t("basics.confirm")}
							onClick={handleSaveFilter}
						/>
					</div>
				</div>
			)}

			{savedFilters.length > 0 ? (
				<div className="flex flex-col gap-1 max-h-[160px] overflow-y-auto bb-scrollbar-darker">
					{savedFilters.map((filter) => (
						<div
							key={filter.id}
							className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer group"
							onClick={() => handleLoadFilter(filter.filterConfig)}
						>
							<div className="flex items-center gap-2 text-sm text-gray-700">
								<Icon className="w-3.5 h-3.5 text-gray-400" icon={faBookmark} />
								{filter.name}
							</div>
							<button
								className="opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity p-1 hover:text-status-error"
								onClick={(e) => {
									e.stopPropagation();
									setFilterToDelete(filter.id);
									setShowDeleteModal(true);
								}}
							>
								<Icon className="w-3.5 h-3.5" icon={faTrashCan} />
							</button>
						</div>
					))}
				</div>
			) : (
				<p className="text-xs text-gray-400 italic">{t("basics.savedFilter.noSavedFilters")}</p>
			)}

			{showDeleteModal && (
				<Modal
					handleClose={() => setShowDeleteModal(false)}
					handleSave={() => {
						if (filterToDelete !== null) {
							handleDeleteFilter(filterToDelete);
						}

						setShowDeleteModal(false);
						setFilterToDelete(null);
					}}
					isOpened={showDeleteModal}
					submitButtonColor={ButtonColor.DANGER}
					submitButtonText={t("basics.delete")}
					title={t("basics.savedFilter.delete")}
				>
					<div className="text-sm text-gray-700">{t("basics.savedFilter.deleteConfirm")}</div>
				</Modal>
			)}
		</div>
	);
};
